/**
 * Presence detection with the front camera (wall profile, SPEC 6.2).
 * A 32×24 grayscale frame once per second, frame difference only. Nothing is sent or stored:
 * the previous frame is the only thing kept in memory.
 */
export const FRAME_W = 32;
export const FRAME_H = 24;

/** Mean absolute difference of two grayscale frames, 0..255. */
export function frameDiff(a: Uint8Array, b: Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += Math.abs(a[i]! - b[i]!);
  return sum / a.length;
}

/** Motion = enough pixels changed noticeably (robust to slow light changes and sensor noise). */
export function isMotion(a: Uint8Array, b: Uint8Array, pixelDelta = 24, minShare = 0.04): boolean {
  let changed = 0;
  for (let i = 0; i < a.length; i++) if (Math.abs(a[i]! - b[i]!) > pixelDelta) changed++;
  return changed / a.length >= minShare;
}

export function toGray(rgba: Uint8ClampedArray, out: Uint8Array): Uint8Array {
  for (let i = 0, j = 0; j < out.length; i += 4, j++)
    out[j] = (rgba[i]! * 77 + rgba[i + 1]! * 150 + rgba[i + 2]! * 29) >> 8;
  return out;
}

export class CameraPresence {
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private prev = new Uint8Array(FRAME_W * FRAME_H);
  private cur = new Uint8Array(FRAME_W * FRAME_H);
  private hasPrev = false;
  lastMotionAt = 0;
  error: string | null = null;

  constructor(private readonly onMotion: () => void) {}

  get running(): boolean {
    return this.stream !== null;
  }

  async start(): Promise<boolean> {
    if (this.stream) return true;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 160 },
          height: { ideal: 120 },
          frameRate: { ideal: 5, max: 10 },
        },
        audio: false,
      });
    } catch (e) {
      this.error = String((e as Error).name ?? e);
      return false;
    }
    this.error = null;
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.srcObject = this.stream;
    await v.play().catch(() => {});
    this.video = v;
    const c = document.createElement("canvas");
    c.width = FRAME_W;
    c.height = FRAME_H;
    this.ctx = c.getContext("2d", { willReadFrequently: true });
    this.hasPrev = false;
    this.timer = setInterval(() => this.sample(), 1000);
    return true;
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    if (this.video) this.video.srcObject = null;
    this.video = null;
    this.ctx = null;
    this.hasPrev = false;
  }

  private sample() {
    if (!this.video || !this.ctx || this.video.readyState < 2) return;
    this.ctx.drawImage(this.video, 0, 0, FRAME_W, FRAME_H);
    toGray(this.ctx.getImageData(0, 0, FRAME_W, FRAME_H).data, this.cur);
    if (this.hasPrev && isMotion(this.prev, this.cur)) {
      this.lastMotionAt = Date.now();
      this.onMotion();
    }
    [this.prev, this.cur] = [this.cur, this.prev];
    this.hasPrev = true;
  }
}
